import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { formatDate } from '../lib/utils';
import { referralSummary } from '../lib/referralSummary';
import { referralRewardLine } from '../lib/referralRewards';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// One labelled value. A missing value shows a dash rather than an empty gap, so a
// blank row reads as "we have no data" instead of as a rendering fault.
const Field = ({ label, children }) => (
  <div className="space-y-0.5">
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="text-sm">{children || <span className="text-muted-foreground">-</span>}</div>
  </div>
);

// A group of related fields. Deliberately unlabelled: every candidate heading here
// ("identity", "contact", "account") would need new translation keys to say something
// the fields already say, and reusing a field's own key as the heading would print
// "Prenume" above a block that is not only the first name. The rule above each group
// is enough to show where one ends and the next begins.
const Section = ({ children }) => (
  <div className="grid grid-cols-2 gap-3 border-t pt-4 first:border-t-0 first:pt-0">
    {children}
  </div>
);

const UserDetailsDialog = ({ user, open, onOpenChange }) => {
  const { t, language } = useLanguage();
  // 'idle' before the first request, 'loading' while it runs, 'error' if it failed.
  // A failed lookup must never render as "no code used" — those are different facts
  // and showing them identically would report a broken request as a finding.
  const [status, setStatus] = useState('idle');
  const [source, setSource] = useState(null);
  // Same idle/loading/ready/error discipline as the referral source: a failed request must
  // not render as "no rewards".
  const [rewardsStatus, setRewardsStatus] = useState('idle');
  const [rewards, setRewards] = useState([]);

  useEffect(() => {
    if (!open || !user) return;

    let cancelled = false;
    setStatus('loading');
    setSource(null);
    setRewardsStatus('loading');
    setRewards([]);

    // Open user A, close it, open user B before A's request returns: A's answer must
    // not land in B's dialog. `user` is in the dependency array, so React tears this
    // effect down and sets THIS closure's `cancelled` before the next one runs — which
    // covers every transition, including reopening on a different user.
    //
    // The subscriptions page needed the `latestOnly` gate for the same hazard because
    // its fetch is fired from an event handler with no effect cleanup to hang a flag
    // on. Here the cleanup exists, so a second guard would be decorative, and a
    // decorative guard invites the next reader to trust it for something it does not do.
    axios.get(`${API}/admin/users/${user.id}/referral-source`).then(
      (response) => {
        if (cancelled) return;
        setSource(response.data);
        setStatus('ready');
      },
      (error) => {
        if (cancelled) return;
        // A failed lookup must never render as "no code used": those are different
        // facts, and showing them identically reports a broken request as a finding.
        console.error('Error fetching referral source:', error);
        setStatus('error');
      }
    );

    axios.get(`${API}/admin/users/${user.id}/referral-rewards`).then(
      (response) => {
        if (cancelled) return;
        setRewards(response.data || []);
        setRewardsStatus('ready');
      },
      (error) => {
        if (cancelled) return;
        console.error('Error fetching referral rewards:', error);
        setRewardsStatus('error');
      }
    );

    return () => { cancelled = true; };
  }, [open, user]);

  if (!user) return null;

  const summary = referralSummary(source);

  const referralLine = () => {
    if (status === 'loading') {
      return <span className="text-muted-foreground">…</span>;
    }
    if (status === 'error') {
      return <span className="text-destructive">{t('error')}</span>;
    }
    if (summary.state === 'none') {
      return <span className="text-muted-foreground">{t('no_referral_code')}</span>;
    }
    if (summary.state === 'referrer_missing') {
      return <span className="text-muted-foreground">{t('referrer_deleted')}</span>;
    }
    return (
      <span>
        {summary.referrerName}{' '}
        <span className="text-muted-foreground">
          ({summary.state === 'counted' ? t('referral_counted') : t('referral_pending')})
        </span>
      </span>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]" data-testid="user-details-dialog">
        <DialogHeader>
          <DialogTitle>{t('user_details')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <Section>
            <Field label={t('first_name')}>{user.first_name}</Field>
            <Field label={t('last_name')}>{user.last_name}</Field>
            <Field label={t('company_name')}>{user.company_name}</Field>
            <Field label={t('cnp_cui')}>{user.cnp_cui}</Field>
          </Section>

          <Section>
            <Field label={t('email')}>{user.email}</Field>
            <Field label={t('phone')}>{user.phone}</Field>
            <Field label={t('email_verified')}>
              <Badge variant={user.email_verified ? 'default' : 'destructive'}>
                {user.email_verified ? t('yes') : t('no')}
              </Badge>
            </Field>
          </Section>

          <Section>
            <Field label={t('address')}>{user.address}</Field>
            <Field label={t('city')}>{user.city}</Field>
            <Field label={t('county')}>{user.county}</Field>
          </Section>

          <Section>
            <Field label={t('is_admin')}>
              <Badge variant={user.is_admin ? 'default' : 'secondary'}>
                {user.is_admin ? t('yes') : t('no')}
              </Badge>
            </Field>
            <Field label={t('viewer')}>
              <Badge variant={user.is_viewer ? 'outline' : 'secondary'}>
                {user.is_viewer ? t('yes') : t('no')}
              </Badge>
            </Field>
            <Field label={t('is_active')}>
              <Badge variant={user.is_active ? 'default' : 'destructive'}>
                {user.is_active ? t('active') : t('inactive')}
              </Badge>
            </Field>
            <Field label={t('created_at')}>
              {user.created_at ? formatDate(user.created_at) : null}
            </Field>
          </Section>

          <Section>
            <Field label={t('referral_code')}>{user.referral_code}</Field>
            <Field label={t('referrals_count')}>{String(user.successful_referrals ?? 0)}</Field>
            <div className="col-span-2">
              <Field label={t('referred_by')}>{referralLine()}</Field>
            </div>
          </Section>

          <div className="space-y-2 border-t pt-4" data-testid="referral-rewards">
            <div className="text-xs text-muted-foreground">{t('referral_rewards')}</div>
            {rewardsStatus === 'loading' && (
              <div className="text-sm text-muted-foreground">…</div>
            )}
            {rewardsStatus === 'error' && (
              <div className="text-sm text-destructive">{t('error')}</div>
            )}
            {rewardsStatus === 'ready' && rewards.length === 0 && (
              <div className="text-sm text-muted-foreground">{t('no_referral_rewards')}</div>
            )}
            {rewardsStatus === 'ready' && rewards.map((entry) => (
              <div key={entry.id} className="text-sm flex flex-wrap gap-x-2">
                <span className="text-muted-foreground">{formatDate(entry.created_at)}</span>
                <span>{referralRewardLine(entry, language)}</span>
                {entry.referred_user_name && (
                  <span className="text-muted-foreground">— {entry.referred_user_name}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UserDetailsDialog;
